/**
 * PRDPreviewPanel - PRD content display with markdown/preview toggle.
 *
 * Features:
 * - Toggle between markdown edit and rendered preview modes
 * - Edit lock when agent is typing (prevents race conditions)
 * - Manual save button for explicit content updates
 *
 * Security: formatMarkdown uses DOMPurify to sanitize all HTML output.
 */

import type { JSX } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { addToast, Button, ButtonGroup, Tooltip } from '@heroui/react';
import { Check, Code, Copy, Eye, Lock, Save } from 'lucide-react';

import { formatMarkdown } from '~/utils/markdown';

import type { ReviewViewMode } from '~/types';

interface PRDPreviewPanelProps {
  content: string;
  projectTitle?: string;
  viewMode: ReviewViewMode;
  isEditLocked: boolean;
  lastChangedSection: string | null; // e.g., "Updated Requirements section"
  onViewModeChange: (mode: ReviewViewMode) => void;
  onContentChange: (content: string) => void;
  onUnsavedChangesChange?: (hasUnsaved: boolean) => void;
  onClearChangedSection: () => void;
}

export function PRDPreviewPanel({
  content,
  isEditLocked,
  lastChangedSection,
  onClearChangedSection,
  onContentChange,
  onUnsavedChangesChange,
  onViewModeChange,
  projectTitle,
  viewMode,
}: PRDPreviewPanelProps): JSX.Element {
  // Local state for optimistic editing
  const [localContent, setLocalContent] = useState(content);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);
  const previewContainerRef = useRef<HTMLDivElement>(null);

  // Sync local content when prop changes (e.g., agent updates)
  useEffect(() => {
    setLocalContent(content);
    setHasUnsavedChanges(false);
  }, [content]);

  // Notify parent of unsaved changes state
  useEffect(() => {
    onUnsavedChangesChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChangesChange]);

  // Highlight effect: wait until agent is done editing before highlighting
  // This prevents the highlight from being destroyed by subsequent content updates
  // while the agent is still making changes to the PRD.
  useLayoutEffect(() => {
    // Don't highlight while agent is still editing - content is unstable
    // and highlights would just be destroyed by subsequent updates
    if (isEditLocked) {
      return;
    }

    // Must have section, be in preview mode, and have container
    if (!lastChangedSection || viewMode !== 'preview' || !previewContainerRef.current) {
      return;
    }

    // Wait for DOM to be stable (localContent synced with content)
    if (localContent !== content) {
      return;
    }

    // If highlight already exists in DOM, don't recreate (it survived the render)
    if (previewContainerRef.current.querySelector('.prd-section-highlight')) {
      return;
    }

    // Parse section name from lastChangedSection
    const sectionMatch =
      lastChangedSection.match(
        /(?:updated|modified|changed|added\s+\w+\s+to)\s+the\s+(.+?)\s+section/i,
      ) ??
      lastChangedSection.match(/to\s+(.+?)\s+section/i) ??
      lastChangedSection.match(/in\s+the\s+(.+?)\s+section/i);
    if (!sectionMatch) return;

    const sectionName = sectionMatch[1]
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

    // Find element
    let element = previewContainerRef.current.querySelector(`#${CSS.escape(sectionName)}`);
    if (!element) {
      element =
        Array.from(previewContainerRef.current.querySelectorAll('[id]')).find(
          el => el.id === sectionName || el.id.endsWith(`-${sectionName}`),
        ) ?? null;
    }
    if (!element) return;

    // Scroll
    const containerEl = previewContainerRef.current;
    const relativeTop =
      element.getBoundingClientRect().top -
      containerEl.getBoundingClientRect().top +
      containerEl.scrollTop;
    containerEl.scrollTo({ top: Math.max(0, relativeTop - 48), behavior: 'smooth' });

    // Collect elements to highlight
    const elementsToHighlight: Element[] = [element];
    const headerMatch = element.tagName.match(/^H([1-6])$/);
    if (headerMatch) {
      const level = parseInt(headerMatch[1], 10);
      let sibling = element.nextElementSibling;
      while (sibling && sibling.tagName !== 'HR') {
        const siblingLevel = sibling.tagName.match(/^H([1-6])$/);
        if (siblingLevel && parseInt(siblingLevel[1], 10) <= level) break;
        elementsToHighlight.push(sibling);
        sibling = sibling.nextElementSibling;
      }
    }

    // Create highlight
    const wrapper = document.createElement('div');
    wrapper.className = 'prd-section-highlight';
    const parent = element.parentElement;
    if (parent) {
      parent.insertBefore(wrapper, element);
      elementsToHighlight.forEach(el => wrapper.appendChild(el));

      // Click to dismiss
      const dismiss = (): void => {
        if (wrapper.parentElement === parent) {
          elementsToHighlight.forEach(el => parent.insertBefore(el, wrapper));
          wrapper.remove();
          onClearChangedSection();
        }
      };
      previewContainerRef.current?.addEventListener('click', dismiss, { once: true });
    }
  }, [isEditLocked, lastChangedSection, content, localContent, viewMode, onClearChangedSection]);

  const handleContentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
      if (isEditLocked) return;
      setLocalContent(e.target.value);
      setHasUnsavedChanges(true);
    },
    [isEditLocked],
  );

  const handleCopyToClipboard = useCallback(async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(localContent);
      setHasCopied(true);
      addToast({
        color: 'success',
        description: 'PRD content copied to clipboard',
      });
      // Reset copy state after 2 seconds
      setTimeout(() => setHasCopied(false), 2000);
    } catch {
      addToast({
        color: 'danger',
        description: 'Failed to copy to clipboard',
      });
    }
  }, [localContent]);

  const handleManualSave = useCallback((): void => {
    if (isEditLocked || !hasUnsavedChanges) return;
    onContentChange(localContent);
    setHasUnsavedChanges(false);
    addToast({
      color: 'success',
      description: 'PRD content saved',
    });
  }, [isEditLocked, hasUnsavedChanges, localContent, onContentChange]);

  const isEditing = viewMode === 'markdown';

  // Approximate token count (~4 chars per token for English text)
  const estimatedTokens = useMemo(() => {
    if (!localContent) return 0;
    return Math.ceil(localContent.length / 4);
  }, [localContent]);

  const formattedTokens = useMemo(() => {
    if (estimatedTokens >= 1000) {
      return `${(estimatedTokens / 1000).toFixed(1)}k`;
    }
    return String(estimatedTokens);
  }, [estimatedTokens]);

  return (
    <div className="flex flex-col h-full w-full">
      {/* Header with mode toggle */}
      <div className="shrink-0 h-[72px] px-4 py-3 border-b border-border bg-surface-elevated flex flex-col justify-center">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <h3 className="text-sm font-medium text-foreground truncate" title={projectTitle}>
              {projectTitle ?? 'PRD Content'}
            </h3>
            {isEditLocked && (
              <Tooltip content="Agent is editing - manual edits disabled">
                <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-warning/10 text-warning text-xs">
                  <Lock className="w-3 h-3" />
                  <span>Locked</span>
                </div>
              </Tooltip>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Copy to clipboard button */}
            <Tooltip content="Copy PRD to clipboard">
              <Button
                isIconOnly
                size="sm"
                variant="flat"
                color="default"
                onPress={() => void handleCopyToClipboard()}
                aria-label="Copy PRD content to clipboard"
              >
                {hasCopied ? (
                  <Check className="w-4 h-4 text-success" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </Tooltip>

            {/* Mode toggle buttons */}
            <ButtonGroup size="sm" variant="flat">
              <Button
                isIconOnly={false}
                color={viewMode === 'markdown' ? 'primary' : 'default'}
                variant={viewMode === 'markdown' ? 'solid' : 'flat'}
                onPress={() => onViewModeChange('markdown')}
                aria-pressed={viewMode === 'markdown'}
              >
                <Code className="w-4 h-4 mr-1.5" />
                Manual Edit
              </Button>
              <Button
                isIconOnly={false}
                color={viewMode === 'preview' ? 'primary' : 'default'}
                variant={viewMode === 'preview' ? 'solid' : 'flat'}
                onPress={() => onViewModeChange('preview')}
                aria-pressed={viewMode === 'preview'}
              >
                <Eye className="w-4 h-4 mr-1.5" />
                Preview
              </Button>
            </ButtonGroup>
          </div>
        </div>
        <div className="flex items-center justify-between mt-0.5">
          <p className="text-xs text-muted">View and edit your PRD content</p>
          {localContent && (
            <Tooltip content="Approximate token count (±10%). Useful for estimating context window usage.">
              <span className="text-xs text-muted tabular-nums">~{formattedTokens} tokens</span>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Content area - explicit width to prevent shrinking */}
      <div className="flex-1 w-full overflow-hidden">
        {isEditing ? (
          <div className="h-full w-full p-4 flex flex-col">
            <textarea
              value={localContent}
              onChange={handleContentChange}
              disabled={isEditLocked}
              className="flex-1 w-full px-4 py-3 text-sm font-mono rounded-lg border border-border bg-surface text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary resize-none disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="PRD content will appear here..."
            />
            <div className="shrink-0 pt-3 flex justify-end">
              <Button
                color="primary"
                size="sm"
                isDisabled={!hasUnsavedChanges || isEditLocked}
                onPress={handleManualSave}
                startContent={<Save className="w-4 h-4" />}
              >
                Save Changes
              </Button>
            </div>
          </div>
        ) : (
          <div ref={previewContainerRef} className="h-full w-full overflow-y-auto p-4">
            {/* Content is sanitized by formatMarkdown using DOMPurify */}
            <div
              className="prose prose-sm max-w-none text-foreground"
              dangerouslySetInnerHTML={{ __html: formatMarkdown(localContent) }}
              aria-label="PRD content preview"
            />
          </div>
        )}
      </div>
    </div>
  );
}
